// src/services/errorReporting.js
// Global error handler for the frontend.
// Catches unhandled JS errors and promise rejections, then reports them to
// the reportClientError Cloud Function, which logs to Google Cloud Error Reporting.
// View errors at: https://console.cloud.google.com/errors (GCP Console)

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase/firebaseConfig';

let _reportFn = null;

const getReportFn = () => {
    if (!_reportFn) {
        const functions = getFunctions(app);
        _reportFn = httpsCallable(functions, 'reportClientError');
    }
    return _reportFn;
};

// Cooldown: avoid flooding on repeated errors (e.g. render loops)
const seen = new Set();
const report = (payload) => {
    const key = `${payload.message}:${payload.url}`;
    if (seen.has(key)) return;
    seen.add(key);
    setTimeout(() => seen.delete(key), 60_000); // reset after 1 min

    getReportFn()({ ...payload, userAgent: navigator.userAgent })
        .catch(() => { /* silent — don't throw from error handler */ });
};

export function initErrorReporting() {
    // Unhandled runtime errors
    window.addEventListener('error', (event) => {
        if (!event.error) return; // Ignore cross-origin script errors with no detail
        report({
            level: 'error',
            message: event.error?.message || event.message || 'Unknown error',
            stack: event.error?.stack,
            url: window.location.href,
        });
    });

    // Unhandled promise rejections (e.g. failed async calls)
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        if (!reason) return;
        report({
            level: 'error',
            message: reason?.message || String(reason) || 'Unhandled promise rejection',
            stack: reason?.stack,
            url: window.location.href,
        });
    });
}

// Call this from React error boundaries to capture component errors
export function reportError(error, componentStack) {
    report({
        level: 'error',
        message: error?.message || String(error) || 'Component error',
        stack: error?.stack,
        componentStack,
        url: window.location.href,
    });
}
